import { IndicatorResume } from './IndicatorResume';
import { IndicatorAreaDatas } from './IndicatorAreaDatas';
import { GraphicAreaData } from './GraphicAreaData';
import { GraphicAreaVolumeData } from './GraphicAreaVolumeData';
import { GraphicLegalBaseVolumeData } from './GraphicLegalBaseVolumeData';
import { GeneralView } from './GeneralView';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const CARD = {
  borderRadius: 14,
  overflow: 'hidden',
  boxShadow: '0 1px 3px var(--vo-escuro-8)',
  marginBottom: 16,
};

const hdr = bg => ({
  padding: '20px 24px',
  background: bg,
  display: 'flex',
  alignItems: 'center',
  gap: 14,
});

const iconCircle = bg => ({
  width: 44, height: 44, borderRadius: '50%',
  background: bg,
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
});

const hTitle = (size = 20) => ({ margin: 0, fontWeight: 700, fontSize: size, color: 'var(--vo-ink)' });
const sectionLbl = { fontSize: 11, fontWeight: 600, color: 'var(--vo-slate)', textTransform: 'uppercase', letterSpacing: '.08em', margin: '0 0 4px' };
const subHdr = (mb = 16) => ({ fontSize: 13, fontWeight: 600, color: 'var(--vo-ink-2)', margin: `0 0 ${mb}px` });

const HouseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--vo-cobalto)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

const BarLinesIcon = ({ stroke = 'var(--vo-cobalto)' }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10"/>
    <line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="14"/>
  </svg>
);

const TableGridIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--vo-cobalto)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <path d="M3 9h18M9 21V9"/>
  </svg>
);

const GreenBarsIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--vo-treatment-store)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 20V10"/>
    <path d="M12 20V4"/>
    <path d="M6 20v-6"/>
  </svg>
);

const EyeIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--vo-cobalto)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

export const ReportsRopaExecutiveSummary = ({ dados }) => {
  const { t } = useTranslation('mapping');

  return (
    <>
      {/* Card 1 — Dados da Empresa */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div style={CARD}>
          <div style={hdr('var(--vo-treatment-collect-grad)')}>
            <div style={iconCircle('color-mix(in srgb, var(--vo-ink-2) 15%, transparent)')}><HouseIcon /></div>
            <h2 style={hTitle(20)}>{t('ropa.executiveSummary.companyData')}</h2>
          </div>
          <div style={{ background: 'var(--vo-treatment-collect-bg)', padding: '16px 24px', borderBottom: '1px solid var(--vo-cloud)' }}>
            <p style={sectionLbl}>{t('ropa.executiveSummary.companyName')}</p>
            <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--vo-ink)' }}>{dados?.companyName}</p>
          </div>
          <div style={{ background: 'white', padding: '16px 24px' }}>
            <p style={sectionLbl}>{t('ropa.executiveSummary.companyResume')}</p>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--vo-ink-2)', lineHeight: 1.65 }}>{dados?.companyDescriptionResume}</p>
          </div>
        </div>
      </motion.div>

      {/* Card 2 — Sumário Executivo */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
        <div style={CARD}>
          <div style={hdr('linear-gradient(135deg,var(--vo-capri-100) 0%,var(--vo-capri-200) 100%)')}>
            <div style={iconCircle('color-mix(in srgb, var(--vo-ink-2) 15%, transparent)')}><BarLinesIcon /></div>
            <h2 style={hTitle(20)}>{t('ropa.executiveSummary.title')}</h2>
          </div>
          <div style={{ background: 'white', padding: 24 }}>
            <p style={subHdr(16)}>{t('ropa.executiveSummary.indicators')}</p>
            <IndicatorResume indicatorResume={dados.indicatorResume} />
            <p style={{ ...subHdr(12), marginTop: 24 }}>{t('ropa.executiveSummary.personalDataByArea')}</p>
            <IndicatorAreaDatas indicatorAreaDatas={dados.indicatorAreaDatas} />
          </div>
        </div>
      </motion.div>

      {/* Card 3 — Uso de Dados Pessoais por Área */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
        <div style={CARD}>
          <div style={hdr('linear-gradient(135deg,var(--vo-capri-100) 0%,var(--vo-capri-200) 100%)')}>
            <div style={iconCircle('color-mix(in srgb, var(--vo-treatment-collect) 15%, transparent)')}><TableGridIcon /></div>
            <h3 style={hTitle(18)}>{t('ropa.executiveSummary.personalDataUseByArea')}</h3>
          </div>
          <div style={{ background: 'white', padding: 24 }}>
            <GraphicAreaData indicatorDataGraphicByArea={dados.indicatorDataGraphicByArea} />
          </div>
        </div>
      </motion.div>

      {/* Card 4 — Volume de Processos e Bases Legais por Área */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}>
        <div style={{ ...CARD }} className="page-break-auto">
          <div style={hdr('linear-gradient(135deg,var(--vo-capri-100) 0%,var(--vo-capri-200) 100%)')}>
            <div style={iconCircle('color-mix(in srgb, var(--vo-ink-2) 15%, transparent)')}><BarLinesIcon /></div>
            <h3 style={hTitle(18)}>{t('ropa.executiveSummary.processVolumeByArea')}</h3>
          </div>
          <div style={{ background: 'white', padding: 24 }}>
            <GraphicAreaVolumeData indicatorVolumeGraphicByArea={dados.indicatorVolumeGraphicByArea} />
          </div>
        </div>
      </motion.div>

      {/* Card 5 — Volume por Base Legal */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}>
        <div style={CARD}>
          <div style={hdr('var(--vo-treatment-store-grad)')}>
            <div style={iconCircle('color-mix(in srgb, var(--vo-treatment-store) 15%, transparent)')}><GreenBarsIcon /></div>
            <h3 style={hTitle(18)}>{t('ropa.executiveSummary.dataAndProcessVolumeByLegalBase')}</h3>
          </div>
          <div style={{ background: 'white', padding: 24 }}>
            <GraphicLegalBaseVolumeData indicatorVolumeGraphicByLegalBase={dados.indicatorVolumeGraphicByLegalBase} />
          </div>
        </div>
      </motion.div>

      {/* Card 6 — Visão Geral */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.5 }}>
        <div style={{ ...CARD }} className="page-break-auto">
          <div style={hdr('var(--vo-treatment-collect-grad)')}>
            <div style={iconCircle('color-mix(in srgb, var(--vo-ink-2) 15%, transparent)')}><EyeIcon /></div>
            <h3 style={hTitle(18)}>{t('ropa.executiveSummary.generalView')}</h3>
          </div>
          <div style={{ background: 'white', padding: 24 }}>
            <GeneralView generalView={dados.generalView} />
          </div>
        </div>
      </motion.div>
    </>
  );
};
